import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useAppDispatch, useAppSelector } from '../../../../flux/stores/hooks';
import { fetchRvtModels, fetchModelGroups } from '../../../../flux/actions/apsThunks';
import { isAccLinkBroken } from '../../../../flux/facades/apsApi';
import { useApiAccessToken } from '../../../../utils/useApiAccessToken';
import { indexNodesByUrn } from '../../../../flux/utils/rvtGraph';
import { ModelGroupCard } from '../ModelGroupCard/ModelGroupCard';
import { ModelGroupEditor } from '../ModelGroupEditor/ModelGroupEditor';
// Shared primitives used by more than one model-groups component
// (ModelGroupCard, ModelGroupEditor, ModelRow) — loaded once, here, at the
// feature's entry point, so it's always present before any of them render.
// Lives one level up: it isn't owned by any single component's folder.
import '../ModelGroups.css';
import './ModelGroupsPanel.css';

const EMPTY_RVT_GRAPH = { status: 'idle', computedAt: null, nodes: [], edges: [], error: null };
const EMPTY_MODEL_GROUPS = { status: 'idle', data: [], error: null };

/**
 * Which models get scanned, and how they're grouped — the admin's next
 * step after `ApsConnectionPanel` shows a healthy link. Renders nothing
 * outside that state: every other connection state already has its own
 * explanation on that panel, and this one has nothing to add there.
 *
 * `/aps/rvt-models` is fetched here purely to back the "links to a model
 * not in this group" tip inside the editor — never to populate the folder
 * browser, which reads ACC directly. So a failure to load it never blocks
 * creating or editing a group; it just means that one tip stays quiet.
 */
export const ModelGroupsPanel = ({ projectId }) => {
    const dispatch = useAppDispatch();
    const { getAccessToken } = useApiAccessToken();
    const aps = useAppSelector(state => state.projects.aps[projectId]);
    const rvtGraph = aps?.rvtGraph ?? EMPTY_RVT_GRAPH;
    const modelGroups = aps?.modelGroups ?? EMPTY_MODEL_GROUPS;

    const [editingId, setEditingId] = useState(null); // null | 'new' | group.object_id

    const graphRequestedRef = useRef(false);
    const groupsRequestedRef = useRef(false);

    const withToken = useCallback(async (run) => {
        try {
            const accessToken = await getAccessToken();
            if (!accessToken) return null;
            return await run(accessToken);
        } catch (error) {
            console.error('Failed to acquire API token for model groups:', error);
            return null;
        }
    }, [getAccessToken]);

    const connection = aps?.connection;
    const isLinked = Boolean(connection?.acc_link && connection.acc_link.state !== 'unlinked');
    const linkBroken = isAccLinkBroken(connection?.acc_link?.state);
    const isConnected = aps?.status === 'loaded' && connection && connection.status !== 'revoked';
    const isHealthy = isConnected && isLinked && !linkBroken;

    useEffect(() => {
        if (!isHealthy) return;
        if (rvtGraph.status !== 'idle' || graphRequestedRef.current) return;
        graphRequestedRef.current = true;
        withToken(accessToken => dispatch(fetchRvtModels({ accessToken, projectId })));
    }, [isHealthy, rvtGraph.status, dispatch, projectId, withToken]);

    useEffect(() => {
        if (!isHealthy) return;
        if (modelGroups.status !== 'idle' || groupsRequestedRef.current) return;
        groupsRequestedRef.current = true;
        withToken(accessToken => dispatch(fetchModelGroups({ accessToken, projectId })));
    }, [isHealthy, modelGroups.status, dispatch, projectId, withToken]);

    const nodesByUrn = useMemo(() => indexNodesByUrn(rvtGraph.nodes), [rvtGraph.nodes]);

    if (!isHealthy) return null;

    const groupsLoading = modelGroups.status === 'idle' || modelGroups.status === 'loading';
    const sortedGroups = [...modelGroups.data].sort((a, b) => a.name.localeCompare(b.name));

    return (
        <div className="mg-panel">
            <div className="mg-panel-header">
                <h3 className="pd-aps-title">Model groups</h3>
            </div>

            {groupsLoading && <p className="projects-loading">Loading model groups…</p>}

            {!groupsLoading && sortedGroups.length === 0 && editingId !== 'new' && (
                <div className="rules-empty">
                    <p>
                        A model group is a named set of models this project scans together. Pick one or
                        more RVT files from the connected ACC project and say what discipline each one is.
                    </p>
                    <button type="button" className="rules-btn-primary" onClick={() => setEditingId('new')}>
                        Create a model group
                    </button>
                </div>
            )}

            {!groupsLoading && sortedGroups.length > 0 && editingId !== 'new' && (
                <div className="pw-actions mg-panel-actions">
                    <button
                        type="button"
                        className="rules-btn-primary"
                        onClick={() => setEditingId('new')}
                        disabled={editingId !== null}
                    >
                        Create a model group
                    </button>
                </div>
            )}

            {editingId === 'new' && (
                <div className="mg-card mg-card-editing">
                    <ModelGroupEditor
                        projectId={projectId}
                        group={null}
                        allGroups={modelGroups.data}
                        nodesByUrn={nodesByUrn}
                        edges={rvtGraph.edges}
                        onDone={() => setEditingId(null)}
                        onCancel={() => setEditingId(null)}
                    />
                </div>
            )}

            {sortedGroups.map(group => (
                <ModelGroupCard
                    key={group.object_id}
                    projectId={projectId}
                    group={group}
                    allGroups={modelGroups.data}
                    nodesByUrn={nodesByUrn}
                    edges={rvtGraph.edges}
                    isEditing={editingId === group.object_id}
                    onStartEdit={() => setEditingId(group.object_id)}
                    onDone={() => setEditingId(null)}
                />
            ))}
        </div>
    );
};
